/**
 * App chrome and the onboarding surfaces, Malayalam. Mirrors every key in
 * `../en/common.ts` — `localeParity.test.ts` fails if it stops doing so.
 *
 * Deliberate non-translations: "KYC", "Pay" and "PAN" stay as they are. The
 * first two are what Kerala savers actually say and read on every other
 * banking app they use, and a transliteration would be less recognisable, not
 * more local. "PAN" stays because that is what the card itself prints; Aadhaar
 * is written ആധാർ, which is the Malayalam spelling of the same word rather
 * than a different one.
 */
export default {
  // Bottom tab bar
  'nav.home': 'ഹോം',
  'nav.schemes': 'പദ്ധതികൾ',
  'nav.pay': 'അടയ്ക്കുക',
  'nav.ledger': 'ലെഡ്ജർ',
  'nav.account': 'അക്കൗണ്ട്',

  // Top app bar
  'header.notifications': 'അറിയിപ്പുകൾ',
  'header.notificationsUnread': 'അറിയിപ്പുകൾ, {{count}} വായിക്കാത്തവ',
  'header.back': 'തിരികെ പോകുക',
  'header.theme.toLight': 'ലൈറ്റ് തീമിലേക്ക് മാറുക',
  'header.theme.toDark': 'ഡാർക്ക് തീമിലേക്ക് മാറുക',
  'header.language': 'ഭാഷ',
  'header.language.en': 'ഇംഗ്ലീഷ്',
  'header.language.ml': 'മലയാളം',

  // Home quick actions
  'quickActions.goals': 'ലക്ഷ്യങ്ങൾ',
  'quickActions.redeem': 'റിഡീം',
  'quickActions.loans': 'വായ്പകൾ',
  'quickActions.complaints': 'പരാതികൾ',

  // Account menu + the page titles those rows lead to
  'account.profile': 'പ്രൊഫൈലും KYC-യും',
  'account.ledger': 'അക്കൗണ്ട് ലെഡ്ജർ',
  'account.goals': 'ലക്ഷ്യങ്ങളും നാഴികക്കല്ലുകളും',
  'account.payments': 'പേയ്‌മെന്റ് ചരിത്രം',
  'account.redemptions': 'റിഡംപ്ഷനുകൾ',
  'account.loans': 'ആഭരണ വായ്പകൾ',
  'account.complaints': 'പരാതികളും പിന്തുണയും',
  'account.notifications': 'അറിയിപ്പുകൾ',
  'account.settings': 'ക്രമീകരണങ്ങൾ',
  'account.help': 'സഹായവും പിന്തുണയും',

  // Profile & KYC screen — the editable D10 demographics (F-24)
  'profile.mobile': 'മൊബൈൽ നമ്പർ',
  'profile.mobileLocked': 'നിങ്ങളുടെ മൊബൈൽ നമ്പറാണ് ലോഗിൻ ഐഡന്റിറ്റി; ഇവിടെ മാറ്റാനാകില്ല.',
  'profile.dateOfBirth': 'ജനന തീയതി',
  'profile.address': 'വിലാസം',
  'profile.addressLine1': 'വീട് / ഫ്ലാറ്റ് നമ്പർ, തെരുവ്',
  'profile.addressLine2': 'പ്രദേശം, സ്ഥലം (നിർബന്ധമില്ല)',
  'profile.addressCity': 'നഗരം',
  'profile.addressState': 'സംസ്ഥാനം',
  'profile.addressPincode': 'പിൻകോഡ്',
  'profile.addressIncomplete': 'വരി 1, നഗരം, സംസ്ഥാനം, 6 അക്ക പിൻകോഡ് എന്നിവ നൽകുക — അല്ലെങ്കിൽ എല്ലാം ഒഴിവാക്കുക.',

  // Profile & KYC screen — chrome (F-35)
  'profile.edit': 'പ്രൊഫൈൽ എഡിറ്റ് ചെയ്യുക',
  'profile.personalInfo': 'വ്യക്തിഗത വിവരങ്ങൾ',
  'profile.fullName': 'പൂർണ്ണനാമം',
  'profile.email': 'ഇമെയിൽ വിലാസം',
  'profile.cancel': 'റദ്ദാക്കുക',
  'profile.save': 'മാറ്റങ്ങൾ സേവ് ചെയ്യുക',
  'profile.saving': 'സേവ് ചെയ്യുന്നു…',
  'profile.submitKyc': 'KYC സമർപ്പിക്കുക',
  'profile.submitKycSubtitle': 'ആധാറും PAN-ഉം, ഓരോ കാർഡിന്റെയും ഫോട്ടോ സഹിതം ചേർക്കുക',
  'profile.kycStanding': 'KYC നില',
  'profile.kycStatus': 'സ്ഥിതി',
  'profile.kycTier': 'ടയർ',
  'profile.kycTransactions': 'ഇടപാടുകൾ',
  'profile.kycEnabled': 'സജീവം',
  'profile.kycNotEnabled': 'സജീവമല്ല',
  'profile.kycLastApproved': 'അവസാനം അംഗീകരിച്ചത്',
  'profile.kycNextReview': 'അടുത്ത പുനഃപരിശോധന',
  'profile.kycUnavailable': 'KYC വിവരങ്ങൾ ഇപ്പോൾ ലഭ്യമല്ല.',

  // KYC standing, as the customer is told it (F-35)
  'kyc.status.notStarted': 'ആരംഭിച്ചിട്ടില്ല',
  'kyc.status.submitted': 'സമർപ്പിച്ചു',
  'kyc.status.underReview': 'പരിശോധനയിലാണ്',
  'kyc.status.approved': 'പരിശോധിച്ചുറപ്പിച്ചു',
  'kyc.status.rejected': 'നിരസിച്ചു',
  'kyc.status.reKycDue': 'പുനഃപരിശോധന വേണം',
  'kyc.status.restricted': 'നിയന്ത്രിതം',
  'kyc.message.notStarted': 'സ്വർണ്ണം സമ്പാദിക്കാൻ തുടങ്ങാൻ നിങ്ങളുടെ KYC പൂർത്തിയാക്കുക.',
  'kyc.message.submitted': 'നിങ്ങളുടെ രേഖകൾ ലഭിച്ചു; പരിശോധനയ്ക്കായി കാത്തിരിക്കുന്നു.',
  'kyc.message.underReview': 'ഞങ്ങളുടെ സംഘം നിങ്ങളുടെ രേഖകൾ പരിശോധിക്കുന്നു. സാധാരണയായി 24 മണിക്കൂർ വരെ എടുക്കും.',
  'kyc.message.approved': 'നിങ്ങളുടെ ഐഡന്റിറ്റി പരിശോധിച്ചുറപ്പിച്ചു. എല്ലാം തയ്യാറാണ്.',
  'kyc.message.amendmentPending': 'നിങ്ങളുടെ ഐഡന്റിറ്റി പരിശോധിച്ചുറപ്പിച്ചതാണ്. രേഖകളിൽ അടുത്തിടെ വരുത്തിയ മാറ്റം പരിശോധനയിലാണ്.',
  'kyc.message.rejected': 'നിങ്ങളുടെ സമർപ്പണം നിരസിച്ചു. രേഖകൾ വീണ്ടും സമർപ്പിക്കുക.',
  'kyc.message.reKycDue': 'കാലാനുസൃതമായ പുനഃപരിശോധന വേണ്ട സമയമായി. രേഖകൾ വീണ്ടും സമർപ്പിക്കുക.',
  'kyc.message.restricted': 'നിങ്ങളുടെ അക്കൗണ്ട് നിയന്ത്രിതമാണ്. ദയവായി പിന്തുണയുമായി ബന്ധപ്പെടുക.',
  'kyc.tier.0': 'ടയർ 0',
  'kyc.tier.1': 'ടയർ 1',
  'kyc.tier.2': 'ടയർ 2',
  'kyc.tier.3': 'ടയർ 3',

  // The identity-capture form (F-35). "Aadhaar" and "PAN" stay as they are — that is what the
  // cards say, in every language this app ships.
  'kyc.aadhaarNumber': 'ആധാർ നമ്പർ',
  'kyc.aadhaarInvalid': 'ആധാർ 12 അക്കങ്ങളാണ്',
  'kyc.aadhaarPhoto': 'ആധാർ കാർഡ് ഫോട്ടോ',
  'kyc.tapToPhotograph': 'ഫോട്ടോ എടുക്കാൻ ടാപ്പ് ചെയ്യുക',
  'kyc.photoPreview': '{{label}} പ്രിവ്യൂ',
  'kyc.removePhoto': 'ഫോട്ടോ നീക്കം ചെയ്യുക',
  'kyc.privacyNote': 'നിങ്ങളുടെ രേഖാ നമ്പറുകളും കാർഡ് ഫോട്ടോകളും പരിശോധനയ്ക്കായി സുരക്ഷിതമായാണ് അയയ്ക്കുന്നത്. നമ്പറുകൾ പ്ലെയിൻ രൂപത്തിൽ ഒരിക്കലും സൂക്ഷിക്കുന്നില്ല; KYC അംഗീകരിച്ചാലുടൻ ഫോട്ടോകൾ നീക്കം ചെയ്യും.',
  'kyc.fileWrongType': 'ആ ഫയൽ ഒരു {{type}} ആണ്. കാർഡ് വീണ്ടും ഫോട്ടോ എടുക്കുക, അല്ലെങ്കിൽ ഒരു JPEG അല്ലെങ്കിൽ PNG തിരഞ്ഞെടുക്കുക.',
  'kyc.fileTooLarge': 'ആ ഫോട്ടോ {{size}} ആണ്, {{limit}} പരിധിയിലും കൂടുതൽ. കാർഡ് മാത്രം, അടുത്തുനിന്ന് ഫോട്ടോ എടുക്കാൻ ശ്രമിക്കുക.',

  // The second officially-valid document, now a choice (F-28). Document names are as the cards
  // print them — a passport says "Passport" in English on the document itself.
  'kyc.secondDocument': 'രണ്ടാമത്തെ രേഖ',
  'kyc.secondDocumentHint': 'ആധാറിനൊപ്പം ഇവയിൽ ഏതെങ്കിലും ഒന്ന്.',
  'kyc.document.PAN': 'PAN കാർഡ്',
  'kyc.document.PASSPORT': 'പാസ്‌പോർട്ട്',
  'kyc.document.DRIVING_LICENCE': 'ഡ്രൈവിംഗ് ലൈസൻസ്',
  'kyc.document.VOTER_ID': 'വോട്ടർ ഐഡി',
  'kyc.documentNumber': '{{document}} നമ്പർ',
  'kyc.documentPhoto': '{{document}} ഫോട്ടോ',
  'kyc.documentInvalid': '{{document}} നമ്പർ ഇങ്ങനെയാണ്: {{example}}',

  // Self-registration wizard (F-35)
  'signup.createAccount': 'അക്കൗണ്ട് സൃഷ്ടിക്കുക',
  'signup.createAccountSubtitle': 'നിങ്ങളുടെ സ്വർണ്ണ സമ്പാദ്യയാത്ര തുടങ്ങുക',
  'signup.optional': '(നിർബന്ധമില്ല)',
  'signup.fullName': 'പൂർണ്ണനാമം',
  'signup.fullNamePlaceholder': 'രവി കുമാർ',
  'signup.email': 'ഇമെയിൽ വിലാസം',
  'signup.emailPlaceholder': 'ravi.kumar@example.com',
  'signup.emailInvalid': 'സാധുവായ ഒരു ഇമെയിൽ വിലാസം നൽകുക',
  'signup.mobile': 'മൊബൈൽ നമ്പർ',
  'signup.mobilePlaceholder': '98765 43210',
  'signup.mobileHint': 'ഈ നമ്പറിലേക്ക് ഞങ്ങൾ ഒരു OTP അയയ്ക്കും',
  'signup.agentCode': 'ഏജന്റ് കോഡ്',
  'signup.agentCodeChecking': 'കോഡ് പരിശോധിക്കുന്നു…',
  'signup.agentCodeResolved': '{{name}} റഫർ ചെയ്തത്',
  'signup.agentCodeResolvedWithPartner': '{{name}} റഫർ ചെയ്തത് — {{partner}}',
  'signup.agentCodeUnknown': 'ഈ കോഡ് സ്ഥിരീകരിക്കാനായില്ല — നിങ്ങൾക്ക് തുടരാം.',
  'signup.back': 'തിരികെ',
  'signup.reviewTitle': 'പരിശോധിച്ച് ഉറപ്പാക്കുക',
  'signup.reviewSubtitle': 'തുടരുന്നതിന് മുൻപ് വിവരങ്ങൾ ഉറപ്പാക്കുക',
  'signup.personalInfo': 'വ്യക്തിഗത വിവരങ്ങൾ',
  'signup.name': 'പേര്',
  'signup.mobileShort': 'മൊബൈൽ',
  'signup.emailShort': 'ഇമെയിൽ',
  'signup.termsBefore': 'ഞാൻ വായിച്ച് അംഗീകരിക്കുന്നു',
  'signup.termsLink': 'നിബന്ധനകളും വ്യവസ്ഥകളും',
  'signup.termsAfter': ', KYC-യ്ക്കായി ഞാൻ നൽകുന്ന തിരിച്ചറിയൽ രേഖകളുടെ പ്രോസസ്സിംഗ് ഉൾപ്പെടെ.',
  'signup.marketing': 'ഓഫറുകളും അപ്‌ഡേറ്റുകളും WhatsApp-ലും ഇമെയിലിലും അയയ്ക്കുക. നിർബന്ധമില്ല.',
  'signup.marketingFromTenant': '{{appName}}-ൽ നിന്നുള്ള ഓഫറുകളും അപ്‌ഡേറ്റുകളും WhatsApp-ലും ഇമെയിലിലും അയയ്ക്കുക. നിർബന്ധമില്ല.',
  'signup.otpNoticeTo': 'ഞങ്ങൾ OTP അയയ്ക്കുന്നത്',
  'signup.kycTitle': 'നിങ്ങളുടെ ഐഡന്റിറ്റി ഉറപ്പാക്കുക',
  'signup.kycSubtitle': 'ആധാറും PAN-ഉം, ഓരോ കാർഡിന്റെയും ഫോട്ടോ സഹിതം ചേർക്കുക. ഇത് പിന്നീട് പ്രൊഫൈലിൽ നിന്നും പൂർത്തിയാക്കാം.',
  'signup.kycSubmit': 'പരിശോധനയ്ക്കായി സമർപ്പിക്കുക',
  'signup.kycUploading': 'അപ്‌ലോഡ് ചെയ്യുന്നു…',
  'signup.kycSkip': 'ഇപ്പോൾ ഒഴിവാക്കുക — പിന്നീട് പ്രൊഫൈലിൽ നിന്ന് പൂർത്തിയാക്കാം',
  'signup.welcome': 'സ്വാഗതം!',
  'signup.welcomeTenant': '{{appName}}-ലേക്ക് സ്വാഗതം!',
  'signup.welcomeKyc': 'നിങ്ങളുടെ അക്കൗണ്ട് തയ്യാറാണ്; KYC രേഖകൾ പരിശോധനയിലാണ്. അംഗീകരിച്ചാലുടൻ തുടങ്ങാം.',
  'signup.welcomeNoKyc': 'നിങ്ങളുടെ അക്കൗണ്ട് വിജയകരമായി സൃഷ്ടിച്ചു. ഇന്നുതന്നെ സ്വർണ്ണ സമ്പാദ്യം തുടങ്ങുക.',
  'signup.goToDashboard': 'ഡാഷ്‌ബോർഡിലേക്ക് പോകുക',
  'signup.pleaseWait': 'ദയവായി കാത്തിരിക്കുക…',
  'signup.sendOtp': 'OTP അയയ്ക്കുക',
  'signup.continue': 'തുടരുക',
  'signup.haveAccount': 'ഇതിനകം അക്കൗണ്ട് ഉണ്ടോ? സൈൻ ഇൻ ചെയ്യുക',

  // Page titles that are not account-menu rows
  'page.pay': 'അടയ്ക്കുക',
  'page.spin': 'സ്പിൻ & വിൻ',
  'page.spinGold': 'ഗോൾഡ് സേവർ സ്പിൻ'
};
